import {
  useCallback,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from "react";

export const MY_PLACE_ID_STORAGE_KEY = "myPlaceId";

function parseStored(raw: string | null): string | undefined {
  if (raw == null) return undefined;
  try {
    const v = JSON.parse(raw) as unknown;
    if (v === null || v === undefined) return undefined;
    if (typeof v === "string") return v;
    return String(v);
  } catch {
    return undefined;
  }
}

function readSession(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return parseStored(sessionStorage.getItem(MY_PLACE_ID_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

function readLocal(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return parseStored(localStorage.getItem(MY_PLACE_ID_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

function getSnapshot(): string | undefined {
  return readSession() ?? readLocal();
}

function getServerSnapshot(): string | undefined {
  return undefined;
}

function subscribe(onStoreChange: () => void) {
  window.addEventListener("storage", onStoreChange);
  return () => window.removeEventListener("storage", onStoreChange);
}

function dispatchPlaceIdStorageEvent(newValue: string | null) {
  window.dispatchEvent(
    new StorageEvent("storage", {
      key: MY_PLACE_ID_STORAGE_KEY,
      newValue,
    }),
  );
}

function writePlaceIdToBothStorages(value: string | undefined) {
  try {
    if (value === undefined || value === null) {
      sessionStorage.removeItem(MY_PLACE_ID_STORAGE_KEY);
      localStorage.removeItem(MY_PLACE_ID_STORAGE_KEY);
      dispatchPlaceIdStorageEvent(null);
    } else {
      const raw = JSON.stringify(value);
      sessionStorage.setItem(MY_PLACE_ID_STORAGE_KEY, raw);
      localStorage.setItem(MY_PLACE_ID_STORAGE_KEY, raw);
      dispatchPlaceIdStorageEvent(raw);
    }
  } catch {
    // quota / private mode
  }
}

export function clearMyPlaceIdStorage() {
  if (typeof window === "undefined") return;
  writePlaceIdToBothStorages(undefined);
}

type SetPlaceIdArg =
  | string
  | undefined
  | ((previous: string | undefined) => string | undefined);

/**
 * - **sessionStorage**: per-tab `placeId` so tabs do not fight each other.
 * - **localStorage**: same key keeps the latest choice for new tabs and return visits.
 *
 * On first load in a tab, session is empty → we copy local → session once so this tab
 * stops following other tabs' local updates until the user changes place here.
 */
export function useMyPlaceIdStorage(): [
  string | undefined,
  (value: SetPlaceIdArg) => void,
] {
  const seededRef = useRef(false);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || seededRef.current) return;
    seededRef.current = true;
    try {
      if (sessionStorage.getItem(MY_PLACE_ID_STORAGE_KEY) != null) return;
      const raw = localStorage.getItem(MY_PLACE_ID_STORAGE_KEY);
      if (raw != null) {
        sessionStorage.setItem(MY_PLACE_ID_STORAGE_KEY, raw);
        dispatchPlaceIdStorageEvent(raw);
      }
    } catch {
      // ignore
    }
  }, []);

  const placeId = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  const setPlaceId = useCallback((value: SetPlaceIdArg) => {
    const previous = getSnapshot();
    const next =
      typeof value === "function"
        ? (value as (p: string | undefined) => string | undefined)(previous)
        : value;
    writePlaceIdToBothStorages(next);
  }, []);

  return [placeId, setPlaceId];
}
